// Original hypothetical cases; these assess calculations, not product suitability.
export const foundationCheckups = [
  {
    id: 'first-plan',
    label: 'First plan',
    title: '把第一份財務計畫算清楚',
    description: '從每月餘裕、預備金到有期限的目標，再看一次費用與下跌的影響。',
    background: [
      '小芸想先整理現金流，再決定要學哪些投資工具。以下金額皆為假設台幣數字，收支維持不變；必要支出已包含在總支出中，不能再扣一次。',
      '她把預備金與目標存款分開，目前目標存款為零，存款規劃不計利息或投資報酬。最後一題另假設已備妥 60,000 元，只做一次壓力演算，不代表建議她投入該方案。',
    ],
    facts: [
      { label: '每月收支', value: '收入 42,000 元；總支出 34,000 元，其中必要支出 30,000 元' },
      { label: '獨立預備金', value: '90,000 元；本次規劃不挪作目標存款' },
      { label: '有期限的目標', value: '10 個月後需要 60,000 元，目前目標存款為 0 元' },
      { label: '另設壓力情境', value: '投入 60,000 元，期末費前市值下跌 10%，再扣一次性費用 600 元' },
    ],
    questions: [
      {
        id: 'first-plan-monthly-surplus', lessonId: 'cash-flow',
        question: '尚未分配給目標或其他用途前，小芸每月的收支餘裕是多少？',
        options: ['8,000 元，收入減去全部支出', '12,000 元，只扣必要支出即可', '6,000 元，直接把目標每月需求當成餘裕'],
        answer: 0,
        explanation: '42,000 − 34,000 = 8,000 元。30,000 元必要支出已包含在 34,000 元內；目標要存多少，是之後如何分配餘裕的問題。',
      },
      {
        id: 'first-plan-reserve-months', lessonId: 'emergency-fund',
        question: '若只以本例每月必要支出衡量，90,000 元預備金約能支應多久？',
        options: ['約 2.65 個月，使用全部支出作分母', '約 11.25 個月，使用每月餘裕作分母', '3 個月，使用每月必要支出作分母'],
        answer: 2,
        explanation: '本題指定用必要支出衡量，所以 90,000 ÷ 30,000 = 3 個月。這只回答這個口徑下的可支應月數，沒有判定 3 個月對每個人都足夠。',
      },
      {
        id: 'first-plan-goal-contribution', lessonId: 'financial-goals',
        question: '不計報酬，每月存入相同金額並在第 10 個月剛好湊足目標，應存多少？存完後每月餘裕剩多少？',
        options: ['每月存 5,000 元，剩 3,000 元', '每月存 6,000 元，剩 2,000 元', '每月存 6,000 元，剩 6,000 元'],
        answer: 1,
        explanation: '60,000 ÷ 10 = 6,000 元；原本每月餘裕 8,000 元，分配後剩 2,000 元。這項規劃沒有動用預備金，也沒有把尚未發生的投資收益算成已經有的錢。',
      },
      {
        id: 'first-plan-stress-after-fee', lessonId: 'costs-and-fraud',
        question: '只看另設的壓力情境：期末再扣 600 元後，可取回多少？假設沒有其他費用、稅與現金流。',
        options: ['54,000 元，只扣價格下跌的影響', '59,400 元，只扣一次性費用', '53,400 元，先反映下跌再扣尚未計入的費用'],
        answer: 2,
        explanation: '60,000 ×（1 − 10%）− 600 = 53,400 元，比 60,000 元目標少 6,600 元。費用與價格風險都會影響可用金額；這是指定情境，不是預測最大損失或實際發生機率。',
      },
    ],
  },
  {
    id: 'fund-comparison',
    label: 'Fund comparison',
    title: '兩份基金資料，先把口徑對齊',
    description: '比較費用估算、配息後總報酬、追蹤差異與換回台幣的成果。',
    background: [
      '阿凱拿到兩個完全假想的基金資料，只練習讀數字，不挑選商品。第一題的固定平均資產額只用來估算年費用，與後面的一單位持有例子分開計算。',
      '基金甲的期末淨值已反映基金內部費用；配息在期末發放並保留為美元現金，沒有再投入。甲與指數採相同期間、美元及含息口徑；換匯題未做匯率避險，忽略額外交易費、換匯費與稅。',
    ],
    facts: [
      { label: '年費用估算', value: '甲年費用率 0.25%；乙 0.65%；各以全年固定平均資產額 160,000 元估算' },
      { label: '甲的一單位持有', value: '期初淨值 50 美元，期末淨值 49.5 美元，期末另配現金 1.5 美元' },
      { label: '同口徑比較基準', value: '該期間指數的美元含息總報酬為 2.4%；追蹤差異定義為基金減指數' },
      { label: '換回台幣', value: '期初 1 美元 = 32 台幣，期末 = 31 台幣；期末持股與配息全部按此匯率換回' },
    ],
    questions: [
      {
        id: 'fund-comparison-estimated-fees', lessonId: 'fund-fees',
        question: '只按第一張資料卡的固定資產額，甲與乙的全年費用估計相差多少？',
        options: ['400 元，等於基金甲的估計費用', '640 元，等於兩項估計費用的差額', '1,040 元，等於基金乙的估計費用'],
        answer: 1,
        explanation: '甲為 160,000 × 0.25% = 400 元，乙為 160,000 × 0.65% = 1,040 元，差額 640 元。這是固定平均資產額的簡化估算，沒有比較兩基金的風險與表現，也不能把這筆費用再扣到已反映內部費用的淨值報酬。',
      },
      {
        id: 'fund-comparison-distribution-return', lessonId: 'etf-distributions',
        question: '基金甲的一單位持有，這段期間的美元總報酬是多少？',
        options: ['2%，把期末淨值與另領現金一起計入', '3%，只用配息除以期初淨值', '−1%，只比較期末與期初淨值'],
        answer: 0,
        explanation: '（49.5 + 1.5 − 50）÷ 50 = 2%。3% 是本例配息相對期初淨值的比例，−1% 是單看淨值變化；兩者各自都不等於完整總報酬。配息沒有再投入，因此只計入一次現金。',
      },
      {
        id: 'fund-comparison-tracking-difference', lessonId: 'tracking-difference',
        question: '沿用甲的美元總報酬，與資料卡的指數比較，能直接算出哪一項？',
        options: ['追蹤差異為 +0.4 個百分點', '追蹤誤差標準差為 0.4%，只要一個期間就能確定', '追蹤差異為 −0.4 個百分點'],
        answer: 2,
        explanation: '基金 2% − 指數 2.4% = −0.4 個百分點。這是本題定義的追蹤差異；追蹤誤差需要多期主動報酬來計算分散程度，不能由這個單期差額直接得到。',
      },
      {
        id: 'fund-comparison-base-currency', lessonId: 'overseas-fx',
        question: '期初用台幣買到一單位甲，期末持股與配息都按指定匯率換回，台幣總報酬是多少？',
        options: ['2%，直接沿用美元總報酬', '−1.125%，直接將美元報酬與匯率變動率相加', '−1.1875%，將美元資產與匯率效果相乘'],
        answer: 2,
        explanation: '期初成本 50 × 32 = 1,600 台幣；期末價值（49.5 + 1.5）× 31 = 1,581 台幣。報酬為（1,581 − 1,600）÷ 1,600 = −1.1875%，亦等於 1.02 ×（31 ÷ 32）− 1。兩個百分比直接相加只得到近似值。',
      },
    ],
  },
];
